import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Search } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { getProjectIssues } from '../../../modules/jira';
import LinkedProjectSelector from '../../elements/common/traceability/LinkedProjectSelector';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 } },
};
const BASE_FILTERS = `issuetype in ("Story", "Task") AND status != Done`;

const buildJQL = (input, projectKey, linkedProjectId) => {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return `project = ${linkedProjectId} AND ${BASE_FILTERS}`;
  const keyRegex = /^[A-Za-z][A-Za-z0-9]+-[0-9]+$/;
  const digitsOnly = /^\d+$/;
  if (digitsOnly.test(trimmed))
    return `project = ${linkedProjectId} AND key = "${projectKey}-${trimmed}" AND ${BASE_FILTERS}`;
  if (keyRegex.test(trimmed))
    return `project = ${linkedProjectId} AND key = "${trimmed}" AND ${BASE_FILTERS}`;
  return `project = ${linkedProjectId} AND summary ~ "${trimmed}*" AND ${BASE_FILTERS}`;
};

const transformIssues = (issues, jiraSite, projectId) =>
  issues.map((issue) => ({
    id: issue.id,
    key: issue.key,
    summary: issue.fields.summary,
    description: issue.fields.description || '',
    issueType: issue.fields.issuetype.name,
    issuestatus: issue.fields.status.status_category?.name || issue.fields.status.name,
    priority: issue.fields.priority?.name || '',
    siteUrl: jiraSite,
    projectId,
  }));

const extractAdfText = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return ' ';
  return (Array.isArray(node.content) ? node.content : [])
    .map(extractAdfText)
    .join(' ');
};

const DESCRIPTION_WORD_THRESHOLD = 30;

const isThinDescription = (description) => {
  if (!description) return true;
  const text =
    typeof description === 'object'
      ? extractAdfText(description)
      : description;
  return text.trim().split(/\s+/).filter(Boolean).length < DESCRIPTION_WORD_THRESHOLD;
};

const DEBOUNCE_MS = 400;

const JiraStorySelector = ({
  currentProject,
  linkedJiraProjects,
  testSuites,
  selectedTestSuites,
  onTestSuitesChange,
  selectedStory,
  onStorySelect,
  generateError,
}) => {
  const { projectId } = useParams();
  const [selectedProject, setSelectedProject] = useState(linkedJiraProjects?.[0] || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const currentProjectRef = useRef(currentProject);
  useEffect(() => { currentProjectRef.current = currentProject; }, [currentProject]);

  const debounceTimer = useRef(null);

  const hasLinkedProject = linkedJiraProjects?.length > 0;

  const handleSearch = useCallback(async (term, project) => {
    if (!project) return;
    setIsLoading(true);
    setErr('');
    try {
      const proj = currentProjectRef.current;
      const jql = buildJQL(term, project.linked_project_key, project.linked_project_id);
      const response = await getProjectIssues(
        { fields: ['summary', 'description', 'status', 'issuetype', 'priority'], jql },
        proj?.id,
      );
      setIssues(
        Array.isArray(response?.issues)
          ? transformIssues(response.issues, proj?.jira_site_url, project.linked_project_id)
          : [],
      );
    } catch {
      setErr('Error fetching issues. Please try again.');
      setIssues([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLinkedProject || !selectedProject) return;
    handleSearch('', selectedProject);
  }, [selectedProject, handleSearch, hasLinkedProject]);

  const scheduleDebouncedSearch = (term, project) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      handleSearch(term, project);
    }, DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  const handleProjectChange = (proj) => {
    setSelectedProject(proj);
    onStorySelect(null);
    setSearchTerm('');
    handleSearch('', proj);
  };

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    scheduleDebouncedSearch(value, selectedProject);
  };

  const handleExplicitSearch = () => {
    clearTimeout(debounceTimer.current);
    handleSearch(searchTerm, selectedProject);
  };

  const showThinWarning = selectedStory && !selectedStory.description === false
    && isThinDescription(selectedStory.description);
  const showNoDescriptionError = selectedStory && !selectedStory.description;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      {!hasLinkedProject ? (
        <Alert severity="warning">
          No Jira project is linked to this project. Please link a Jira project first.
        </Alert>
      ) : (
        <>
          <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); handleExplicitSearch(); }}
            sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Box sx={{ minWidth: 200 }}>
              <LinkedProjectSelector
                linkedProjects={linkedJiraProjects}
                onProjectChange={handleProjectChange}
                selectedProjectId={selectedProject?.linked_project_id}
              />
            </Box>
            <TextField
              placeholder="Search by key, ID, or summary"
              value={searchTerm}
              onChange={handleSearchTermChange}
              fullWidth
              size="small"
              inputProps={{ 'aria-label': 'Search Jira issues' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleExplicitSearch}
                      disabled={isLoading}
                      edge="end"
                      size="small"
                      aria-label="Run search"
                      sx={{ border: '1px solid #ced4da' }}
                    >
                      {isLoading ? <CircularProgress size={16} /> : <Search fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {err && <Alert severity="error" sx={{ py: 0 }}>{err}</Alert>}

          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              maxHeight: 200,
              overflowY: 'auto',
              bgcolor: '#fafafa',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : issues.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No issues found</Typography>
              </Box>
            ) : (
              issues.map((issue) => {
                const isSelected = selectedStory?.id === issue.id;
                return (
                  <Box
                    key={issue.id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => onStorySelect(isSelected ? null : issue)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStorySelect(isSelected ? null : issue);
                      }
                    }}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      bgcolor: isSelected ? 'primary.50' : 'transparent',
                      borderLeft: '3px solid',
                      borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                      outline: 'none',
                      '&:hover': { bgcolor: isSelected ? 'primary.50' : '#f5f5f5' },
                      '&:focus-visible': { boxShadow: 'inset 0 0 0 2px #3b82f6' },
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'primary.main', minWidth: 80, flexShrink: 0 }}
                      >
                        {issue.key}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {issue.summary}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '11px' }}
                      >
                        {issue.issuestatus}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {selectedStory && (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: showNoDescriptionError
                  ? 'error.main'
                  : showThinWarning
                    ? 'warning.main'
                    : 'primary.200',
                borderRadius: 1,
                bgcolor: showNoDescriptionError
                  ? '#fff5f5'
                  : showThinWarning
                    ? '#fffbeb'
                    : 'primary.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="primary.main">
                  {selectedStory.key}
                </Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{selectedStory.issueType}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{selectedStory.issuestatus}</Typography>
              </Box>

              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {selectedStory.summary}
              </Typography>

              {selectedStory.description ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {typeof selectedStory.description === 'object'
                    ? '(Structured description — will be passed to AI)'
                    : selectedStory.description}
                </Typography>
              ) : (
                <Typography variant="caption" color="error.main">
                  This story has no description. Add a description in Jira before importing.
                </Typography>
              )}

              {showThinWarning && !showNoDescriptionError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.dark">
                    This story may not have enough detail for quality test cases. You can still proceed.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

         <Box>
          <Form.Label className="required" style={{ fontWeight: 500, fontSize: 14 }}>
            Test Suite
          </Form.Label>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Applies to all generated test cases
          </Typography>
          <FormControl sx={{ width: '100%' }}>
            <Select
              multiple
              displayEmpty
              value={selectedTestSuites}
              onChange={(e) => {
                const val = e.target.value;
                onTestSuitesChange(typeof val === 'string' ? val.split(',') : val);
              }}
              input={<OutlinedInput />}
              IconComponent={KeyboardArrowDownIcon}
              inputProps={{ 'aria-label': 'Select test suites' }}
              renderValue={(selected) =>
                selected.length === 0
                  ? <span style={{ color: '#9e9e9e' }}>Select test suite(s)</span>
                    : testSuites
                      .filter((s) => selected.includes(s.id))
                      .map((s) => s.name)
                      .join(', ')
              }
              MenuProps={MenuProps}
              sx={{
                maxHeight: '48px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
              }}
            >
              {testSuites?.map((suite) => (
                <MenuItem key={suite.id} value={suite.id} disabled={suite.status === 'suspended'}>
                  <Checkbox
                    disableRipple
                    checked={selectedTestSuites.includes(suite.id)}
                    disabled={suite.status === 'suspended'}
                  />
                  <ListItemText
                    primary={
                      <span>
                        {suite.name}{' '}
                        {suite.status === 'suspended' && (
                          <span style={{ fontSize: '10px', color: 'red' }}>(Suspended)</span>
                        )}
                      </span>
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <a
            href={`/project/${projectId}/testsuites`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: '#1976d2', textDecoration: 'none' }}
          >
            + Add test suite
          </a>
        </Box>

          {selectedStory && selectedTestSuites.length > 0 && !showNoDescriptionError && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="primary.main">
                AI will generate multiple test cases for this story
              </Typography>
            </Box>
          )}

          {generateError && <Alert severity="error" sx={{ py: 0 }}>{generateError}</Alert>}
        </>
      )}
    </Box>
  );
};

export default JiraStorySelector;